import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Loader2, ArrowLeft, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VendorApplyPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [form, setForm] = useState({
    first_name: searchParams.get('firstName') || searchParams.get('first_name') || '',
    last_name: searchParams.get('lastName') || searchParams.get('last_name') || '',
    email: searchParams.get('email') || '',
    phone: (searchParams.get('phone') || '').replace(/^\+?1/, '').trim(),
    business_name: '',
    password: '',
    confirm_password: '',
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.phone || !form.business_name || !form.password) {
      setError('Please fill in all required fields.');
      return;
    }
    if (form.password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (form.password !== form.confirm_password) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/vendor-apply/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: form.first_name,
          last_name: form.last_name,
          email: form.email,
          phone: form.phone,
          business_name: form.business_name,
          password: form.password,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Submission failed');
      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full text-center"
        >
          <div className="w-16 h-16 bg-green-500/10 border border-green-500/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-400" />
          </div>
          <h1 className="text-2xl font-semibold text-white mb-3">Application Submitted</h1>
          <p className="text-white/50 text-sm leading-relaxed mb-6">
            Thanks for applying! Our team will review your information and you'll be able to log in once approved.
          </p>
          <button
            onClick={() => navigate('/vendor/login')}
            className="text-gold hover:text-gold/80 text-sm transition-colors"
            data-testid="go-to-login-btn"
          >
            Go to Vendor Login
          </button>
        </motion.div>
      </div>
    );
  }

  const inputCls = "w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2.5 rounded-md focus:outline-none focus:border-white/30 transition-colors";

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-lg w-full"
      >
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-1.5 text-white/40 hover:text-white/70 text-xs mb-8 transition-colors"
          data-testid="back-home-btn"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to site
        </button>

        <h1 className="text-3xl font-semibold text-white mb-1" data-testid="apply-heading">Vendor Application</h1>
        <p className="text-white/40 text-sm mb-8">Join our network of exotic animal and event vendors.</p>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-white/50 text-xs mb-1 block">First Name *</label>
              <input
                name="first_name" value={form.first_name} onChange={handleChange} required
                className={inputCls}
                data-testid="apply-first-name"
              />
            </div>
            <div>
              <label className="text-white/50 text-xs mb-1 block">Last Name *</label>
              <input
                name="last_name" value={form.last_name} onChange={handleChange} required
                className={inputCls}
                data-testid="apply-last-name"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-white/50 text-xs mb-1 block">Email *</label>
              <input
                name="email" type="email" value={form.email} onChange={handleChange} required
                className={inputCls}
                data-testid="apply-email"
              />
            </div>
            <div>
              <label className="text-white/50 text-xs mb-1 block">Phone *</label>
              <input
                name="phone" value={form.phone} onChange={handleChange} required
                className={inputCls}
                data-testid="apply-phone"
              />
            </div>
          </div>

          <div>
            <label className="text-white/50 text-xs mb-1 block">Business Name *</label>
            <input
              name="business_name" value={form.business_name} onChange={handleChange} required
              className={inputCls}
              data-testid="apply-business-name"
            />
          </div>

          {/* Account Setup */}
          <div className="pt-3 border-t border-white/5">
            <h3 className="text-white/70 text-xs font-medium uppercase tracking-wider mb-3">Set Up Your Account</h3>
          </div>

          <div>
            <label className="text-white/50 text-xs mb-1 block">Password *</label>
            <div className="relative">
              <input
                name="password" type={showPassword ? 'text' : 'password'} value={form.password} onChange={handleChange} required
                placeholder="At least 6 characters"
                className={`${inputCls} pr-10 placeholder-white/20`}
                data-testid="apply-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors"
                data-testid="toggle-password"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="text-white/50 text-xs mb-1 block">Confirm Password *</label>
            <input
              name="confirm_password" type={showPassword ? 'text' : 'password'} value={form.confirm_password} onChange={handleChange} required
              className={inputCls}
              data-testid="apply-confirm-password"
            />
          </div>

          <p className="text-white/30 text-xs">
            You'll be able to set up your animals, hourly rates, and packages after your account is approved.
          </p>

          {error && <p className="text-red-400 text-xs" data-testid="apply-error">{error}</p>}

          <button
            type="submit" disabled={loading}
            className="w-full py-3 bg-white text-[#0a0a0a] font-semibold text-sm rounded-md hover:bg-white/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="apply-submit-btn"
          >
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</> : 'Submit Application'}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
